import React from 'react';
import { IProps as IBlockProps } from './GlobalHeader.interface';
import { GlobalHeader } from './GlobalHeader.component';
import TranslationComponent from '@/utils/TranslationComponent/TranslationComponent';
import * as translation from '@/utils/TranslationComponent/translation.utilities';
import { IDonationWidgetProps } from '../DonationBlock/DonationBlock.interface';
import { INavItem } from './GlobalHeader.interface';


export interface IImageProps {
    alternateText: string;
    defaultSrc: string;
    sizesAttribute: string[];
    srcSet: string[];
    srcSetWebp: string[];
    isBackground: boolean;
    textContent: string;
    isLazy: boolean;
}

export interface ILink {
    text: string;
    target: string | null;
    href: string;
    title: string | null;
    ariaLabel: string | null;
}

export interface INavigationLink extends ILink {
    subLink: boolean;
    links?: INavigationLink[] | null;
}

export interface IMegaNavigationGroupBlock {
    type: 'MegaNavigationGroupBlock';
    heading: ILink;
    links: INavigationLink[] | null;
}

export interface IButtonProps {
    link: ILink;
    btnStyle: string | null;
    icon: string | null;
    iconPosition: string | null;
    eventName: string | null;
}

export interface IImageBox {
    image: IImageProps;
    title: string;
    button: IButtonProps;
}

export interface IMegaNavigationGroupContainer {
    type: 'MegaNavigationGroupContainer';
    heading: ILink;
    items: IMegaNavigationGroupBlock[];
    imageBox: IImageBox | null;
}

export interface IMegaNavigationLinkBlock {
    type: 'MegaNavigationLinkBlock';
    link: ILink;
}

export type IMegaNavigationItem =
    | IMegaNavigationGroupContainer
    | IMegaNavigationLinkBlock;

export interface IDonationAmount {
    amount: number | null;
    tooltip: string;
    currencySymbol: string;
    customAmountLabel: string | null;
}

export interface IStripePaymentSettings {
    publicApiKey: string;
    generateOrderApiUrl: string;
}

export interface IPayPalPaymentSettings {
    clientId: string;
    scriptUrl: string;
    orderCreateApiUrl: string;
    orderApproveApiUrl: string;
}

export interface IDonationBlockProps {
    formId: number;
    campaignId: string;
    currencyCode: string;
    currencySymbol: string;
    oneOffTabLabel: string;
    minimumOneOffAmount: number;
    maximumOneOffAmount: number;
    oneoffAmounts: IDonationAmount[];
    monthlyTabLabel: string;
    minimumMonthlyAmount: number;
    maximumMonthlyAmount: number;
    monthlyAmounts: IDonationAmount[];
    amountLabel: string;
    otherAmountLabel: string;
    creditCardOptionLabel: string;
    payPalOptionLabel: string;
    donateButtonLabel: string;
    confirmationPage: string;
    footerIcons: string[];
    alternativePaymentLabel: string;
    alternativePaymentLink: string;
    stripePaymentSettings: IStripePaymentSettings;
    payPalPaymentSettings: IPayPalPaymentSettings;
}

export interface IInputDonationStories {
    parentClass: string | null;
    heading: string;
    items: ILink[];
    iconName: string;
}

export interface IWaysToHelp {
    heading?: string;
    summary?: string;
    items: {
        image: any;
        link: any;
    }[];
}

export interface IBlockInputProps {
    logo: IImageProps;
    items: INavItem[];
    donationBlock: IDonationBlockProps | null;
    waysToHelp: IWaysToHelp | null;
    exitUrl: string | null;
    donationStories: IInputDonationStories;
    autoCompleteApiUrl: string;
    autoCompleteRedirectUrl: string;
    autoCompletePlaceholder: string;
    autoCompleteMobilePlaceholder: string;
    autoCompleteButtonText?: string;
    id: string | null;
    paddingTop: number | null;
    paddingBottom: number | null;
}

const mapInputToProps = (input: IBlockInputProps): IBlockProps => {
    const {
        id,
        logo,
        items,
        donationBlock,
        waysToHelp,
        donationStories,
        autoCompleteApiUrl,
        autoCompleteMobilePlaceholder,
        autoCompleteButtonText = 'Search',
        exitUrl,
        autoCompletePlaceholder,
        autoCompleteRedirectUrl,
        paddingBottom,
        paddingTop,
        ...rest
    } = input;

    return {
        logo: logo ? translation.mapImageProps(logo) : undefined,
        navItems: items?.map((item) => ({
            ...item,
            ...(item.type === 'dropdown' && item.promo ? {
                promo: {
                    ...item.promo,
                    button: item.promo.button ? translation.mapAnchorProps(item.promo.button) : undefined,
                }
            } : {}),
        })),
        donateButton: undefined,
        searchButton: undefined,
        donationBlock: donationBlock as IDonationWidgetProps,
        waysToHelp: {
            title: waysToHelp?.heading || undefined,
            content: waysToHelp?.summary || undefined,
            items:
                waysToHelp?.items.map((item) => ({
                    image: item?.image ? translation.mapImageProps(item.image) : null,
                    button: item?.link ? translation.mapAnchorProps(item.link) : null,
                })) || [],
        },
        donationStories: donationStories
            ? {
                title: donationStories.heading || undefined,
                description: undefined,
                items:
                    donationStories.items && donationStories.items.length > 0
                        ? donationStories.items.map((item) => {
                            const { text, href, target, title, ariaLabel, ...rest } =
                                item;
                            return {
                                children: text,
                                href: href,
                                target: target || undefined,
                                title: title || undefined,
                                'aria-label': ariaLabel || undefined,
                                ...rest,
                            };
                        })
                        : undefined,
            }
            : undefined,
        autoCompleteApiUrl,
        autoCompleteButtonText,
        autoCompleteMobilePlaceholder,
        autoCompletePlaceholder,
        autoCompleteRedirectUrl,
        ...rest,
    };
};

const GlobalHeaderTranslation: React.FC<IBlockInputProps> = (inputProps) => (
    <TranslationComponent
        inputProps={inputProps}
        TargetComponent={GlobalHeader}
        mapProps={mapInputToProps}
    />
);

export default GlobalHeaderTranslation;
